import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { HiDocument, HiChevronRight, HiDownload, HiFilter, HiDocumentText, HiPhotograph } from 'react-icons/hi'
import SEO from '../../components/SEO/SEO'
import { seoBreadcrumbPair } from '../../utils/seoUtils'
import { useLocalizedLink } from '../../hooks/useLocalizedLink'
import './Documents.css'

const DOCUMENT_ITEMS = [
  {
    id: 'internal-charter-2026',
    categoryId: 'statutory',
    titleKey: 'documents.charter2026.title',
    dateKey: 'documents.charter2026.date',
    pagePath: '/documents/charter'
  },
  {
    id: 'attestation-guidelines-2026',
    categoryId: 'certification',
    titleKey: 'documents.attestationGuidelines2026.title',
    dateKey: 'documents.attestationGuidelines2026.date',
    pagePath: '/documents/attestation-guidelines'
  },
  {
    id: 'logo-suma-international',
    categoryId: 'brand',
    titleKey: 'documents.logos.suma.title',
    dateKey: 'documents.logos.suma.date',
    downloadUrl: '/documents/logos/suma-international.png',
    downloadName: 'suma-international-logo.png',
    previewUrl: '/logo-isuma.png'
  },
  {
    id: 'logo-kubo-federation',
    categoryId: 'brand',
    titleKey: 'documents.logos.kubo.title',
    dateKey: 'documents.logos.kubo.date',
    downloadUrl: '/documents/logos/kubo-federation.png',
    downloadName: 'kubo-federation-logo.png',
    previewUrl: '/images/federations/KUBO Federation.png'
  },
  {
    id: 'logo-karatejutsu-federation',
    categoryId: 'brand',
    titleKey: 'documents.logos.karatejutsu.title',
    dateKey: 'documents.logos.karatejutsu.date',
    downloadUrl: '/documents/logos/karatejutsu-federation.png',
    downloadName: 'karatejutsu-federation-logo.png',
    previewUrl: '/images/federations/Karate-jutsu Federation.png'
  },
  {
    id: 'logo-jujutsu-federation',
    categoryId: 'brand',
    titleKey: 'documents.logos.jujutsu.title',
    dateKey: 'documents.logos.jujutsu.date',
    downloadUrl: '/documents/logos/jujutsu-federation.png',
    downloadName: 'jujutsu-federation-logo.png',
    previewUrl: '/images/federations/Jujutsu Federation.png'
  },
  {
    id: 'logo-aikibujutsu-federation',
    categoryId: 'brand',
    titleKey: 'documents.logos.aikibujutsu.title',
    dateKey: 'documents.logos.aikibujutsu.date',
    downloadUrl: '/documents/logos/aikibujutsu-federation.png',
    downloadName: 'aikibujutsu-federation-logo.png',
    previewUrl: '/images/federations/Aiki-bu-jutsu Federation.png'
  }
]

function Documents() {
  const { t } = useTranslation()
  const getLocalizedLink = useLocalizedLink()
  const [selectedCategory, setSelectedCategory] = useState('all')

  const breadcrumbItems = useMemo(
    () => seoBreadcrumbPair(t('common.home'), t('common.documents'), 'documents'),
    [t]
  )

  const categories = [
    { id: 'all', name: t('documents.all', 'All Documents') },
    { id: 'statutory', name: t('documents.statutory', 'Statutory') },
    { id: 'regulatory', name: t('documents.regulatory', 'Regulatory') },
    { id: 'certification', name: t('documents.certification', 'Certification') },
    { id: 'forms', name: t('documents.forms', 'Forms') },
    { id: 'protocols', name: t('documents.protocols', 'Protocols') },
    { id: 'international', name: t('documents.international', 'International') },
    { id: 'methodological', name: t('documents.methodological', 'Methodological') },
    { id: 'brand', name: t('documents.brand', 'Brand assets') }
  ]

  const documents = useMemo(() => {
    return DOCUMENT_ITEMS.filter(
      (doc) => selectedCategory === 'all' || doc.categoryId === selectedCategory
    )
  }, [selectedCategory])

  return (
    <div className="documents-page">
      <SEO
        title={t('common.documents')}
        description={t('documents.description', 'Official documents of SUMA International')}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="container">
        <div className="documents-hero">
          <HiDocument className="hero-icon" />
          <h1>{t('common.documents')}</h1>
          <p className="documents-intro">
            {t(
              'documents.intro',
              'Access official documents, forms, protocols, and other materials from SUMA International.'
            )}
          </p>
        </div>

        <div className="documents-filters">
          <HiFilter className="filter-icon" />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-select"
          >
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <div className="documents-list">
          {documents.length > 0 ? (
            documents.map((doc) => {
              const isAsset = Boolean(doc.downloadUrl)
              const PlaceholderIcon = isAsset ? HiPhotograph : HiDocumentText

              return (
                <div key={doc.id} className="document-card">
                  {doc.previewUrl ? (
                    <div className="document-preview">
                      <img src={doc.previewUrl} alt="" loading="lazy" decoding="async" />
                    </div>
                  ) : (
                    <div className="document-preview-placeholder">
                      <PlaceholderIcon className="document-icon" />
                    </div>
                  )}
                  <div className="document-info">
                    <h3>{t(doc.titleKey)}</h3>
                    <p className="document-category">{t(`documents.${doc.categoryId}`, doc.categoryId)}</p>
                    <p className="document-date">{t(doc.dateKey)}</p>
                  </div>
                  <div className="document-actions">
                    {doc.pagePath ? (
                      <Link to={getLocalizedLink(doc.pagePath)} className="btn btn-secondary">
                        {t('documents.openPage', 'Open')}
                        <HiChevronRight className="btn-icon" />
                      </Link>
                    ) : (
                      <a href={doc.downloadUrl} download={doc.downloadName} className="btn btn-primary">
                        <HiDownload className="btn-icon" />
                        {t('documents.download', 'Download')}
                      </a>
                    )}
                  </div>
                </div>
              )
            })
          ) : (
            <div className="no-documents">
              <HiDocument className="no-documents-icon" />
              <p>{t('documents.noDocuments', 'Documents will be displayed here as they are added to the system.')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Documents
